import i18nFactory from '@yandex-int/i18n';
import { Epic } from 'redux-observable';
import { concat, EMPTY, Observable, of } from 'rxjs';

import { Action } from '../../../actions';
import { requestAuthorizationData, setUserLogin, updateDocumentTitle } from '../../../actions/common';
import {
    FETCH_FREELANCER_CARD_DATA,
    FETCH_FREELANCER_CARD_REGISTERED_DATA,
    FetchFreelancerCardAction,
    fetchFreelancerCardRegisteredData,
    FetchFreelancerCardRegisteredDataAction,
    handleUpdateFreelancerStatusError,
    handleUpdateFreelancerStatusSuccess,
    releaseFreelancerCard,
    setFreelancerCardError,
    setFreelancerCardNotFound,
    UPDATE_FREELANCER_STATUS,
    updateFreelancerCard,
    updateFreelancerCardPrivateData,
    updateFreelancerCustomerData,
    UpdateFreelancerStatusAction,
    WITHDRAW_REQUEST_BY_CLIENT_START,
    withdrawRequestByClientError,
    WithdrawRequestByClientStartAction,
    withdrawRequestByClientSuccess
} from '../../../actions/freelancers/freelancer-card';
import { fetchHeaderInfo } from '../../../actions/header';
import {
    mapFreelancerCustomerData,
    mapFreelancerPrivateData,
    mapFreelancerPublicResponse,
    mapPublicApiOperator
} from '../../../mappers/freelancers';
import { showNoticeMessage } from '../../../service/GridHelpers/GridHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { GdFreelancerStatus } from '../../../typings/graphql-grid';
import { NoticeType } from '../../../typings/grid';
import { State } from '../../../typings/state';

import * as keyset from './freelancer-card.i18n';

const i18n = i18nFactory(keyset);

export const freelancerCardEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_FREELANCER_CARD_DATA).mergeMap(
        (action: FetchFreelancerCardAction): Observable<Action> => {
            return Observable.fromPromise(deps.incognitoDataProvider.fetchFreelancerPublicData(action.freelancerLogin))
                .mergeMap(
                    (primaryResponse): Observable<Action> => {
                        const { freelancersList, operator } = primaryResponse;
                        const [freelancer] = freelancersList;

                        let observables: Observable<Action>;
                        let freelancerCardRelease$: Observable<Action> = of(setFreelancerCardNotFound());

                        let updateFreelancerCard$: Observable<Action> = EMPTY;

                        if (freelancer) {
                            const freelancerData = mapFreelancerPublicResponse(freelancer);
                            updateFreelancerCard$ = concat(
                                of(updateFreelancerCard(freelancerData, mapPublicApiOperator(operator))),
                                of(
                                    updateDocumentTitle(
                                        `${freelancerData.fullName} - ${i18n('freelancer-page-header')}`
                                    )
                                )
                            );

                            if (freelancer.card) {
                                freelancerCardRelease$ = of(releaseFreelancerCard());
                            }
                        }

                        if (!operator) {
                            observables = concat(updateFreelancerCard$, of(fetchHeaderInfo()), freelancerCardRelease$);
                        } else if (!operator.clientId) {
                            observables = concat(
                                updateFreelancerCard$,
                                of(fetchHeaderInfo()),
                                of(setUserLogin(operator.login)),
                                freelancerCardRelease$
                            );
                        } else {
                            observables = concat(
                                updateFreelancerCard$,
                                of(requestAuthorizationData()),
                                of(
                                    fetchFreelancerCardRegisteredData(
                                        action.freelancerLogin,
                                        operator.login,
                                        Boolean(freelancer && freelancer.card)
                                    )
                                )
                            );
                        }
                        return observables;
                    }
                )
                .catch((err: Error): Observable<Action> => of(setFreelancerCardError(err)));
        }
    );
};

export const freelancerCardRegisteredEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_FREELANCER_CARD_REGISTERED_DATA).mergeMap(
        (action: FetchFreelancerCardRegisteredDataAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.entitiesDataProvider.fetchFreelancerRegisteredData(action.freelancerLogin, action.login)
            )
                .mergeMap(
                    (data): Observable<Action> => {
                        const freelancerPrivateData = mapFreelancerPrivateData(data);
                        const customerData = mapFreelancerCustomerData(data);

                        if (freelancerPrivateData) {
                            return concat(
                                of(updateFreelancerCustomerData(customerData)),
                                of(updateFreelancerCardPrivateData(freelancerPrivateData)),
                                of(releaseFreelancerCard())
                            );
                        } else if (!action.freelancerCardReady) {
                            return of(setFreelancerCardNotFound());
                        }

                        return concat(of(updateFreelancerCustomerData(customerData)), of(releaseFreelancerCard()));
                    }
                )
                .catch(err => of(setFreelancerCardError(err)));
        }
    );
};

export const updateFreelancerStatusEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_FREELANCER_STATUS).mergeMap(
        (action: UpdateFreelancerStatusAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.updateFreelancerStatus({
                    input: {
                        status: GdFreelancerStatus[action.status]
                    }
                })
            )
                .map(response => response.updateFreelancerStatus.freelancer)
                .map(data => handleUpdateFreelancerStatusSuccess(data.status))
                .catch(() => {
                    return of(handleUpdateFreelancerStatusError());
                });
        }
    );
};

export const withdrawRequestByClientEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(WITHDRAW_REQUEST_BY_CLIENT_START).mergeMap(
        (action: WithdrawRequestByClientStartAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.cancelFlServiceByClient({
                    freelancerId: action.freelancerId,
                    projectId: action.projectId
                })
            )
                .map(() => withdrawRequestByClientSuccess())
                .do(() => showNoticeMessage(NoticeType.success, i18n('withdraw-coop-success')))
                .catch(() => {
                    showNoticeMessage(NoticeType.error, i18n('error-message'));
                    return of(withdrawRequestByClientError());
                });
        }
    );
};
